// Shared helpers for reading recordings back into a session view.
//
// Both the live "This session" log (record screen) and the past-session detail
// screen read the same flat `recordings` collection, deduped to the latest take
// per question. Keeping that logic in one place means the two screens can never
// drift on how a re-record supersedes an earlier answer.

import type { DocumentData, QueryDocumentSnapshot } from "firebase/firestore";

export interface Take {
  id: string;
  data: DocumentData;
}

/** Milliseconds for ordering; a pending write has no server timestamp yet, so
 *  treat it as the newest so an in-flight re-record wins over the take it
 *  replaces. */
export function docMillis(data: DocumentData): number {
  const ts = data.createdAt as { toMillis?: () => number } | null | undefined;
  return ts?.toMillis?.() ?? Number.POSITIVE_INFINITY;
}

/**
 * Collapse a session's recordings to the latest take per question, ordered by
 * questionOrder. A re-record makes a fresh doc, so several docs can share a
 * questionId; we keep the newest. Equality-only queries feed this (no orderBy),
 * so no composite index is needed.
 */
export function latestTakes(
  docs: QueryDocumentSnapshot<DocumentData>[],
): Take[] {
  const latest = new Map<string, Take>();
  for (const d of docs) {
    const data = d.data();
    const qid = (data.questionId as string) ?? d.id;
    const prev = latest.get(qid);
    if (!prev || docMillis(data) >= docMillis(prev.data)) {
      latest.set(qid, { id: d.id, data });
    }
  }
  return [...latest.values()].sort(
    (a, b) =>
      ((a.data.questionOrder as number) ?? 0) -
      ((b.data.questionOrder as number) ?? 0),
  );
}

/** A Firestore Timestamp → Date, tolerating a not-yet-committed server value. */
function toDate(ts: unknown): Date {
  const t = ts as { toDate?: () => Date } | null | undefined;
  return t?.toDate?.() ?? new Date();
}

export function formatDate(ts: unknown): string {
  return toDate(ts).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function formatTime(ts: unknown): string {
  return toDate(ts).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Sort key for the session list — newest first; pending writes float to top. */
export function startedMillis(data: DocumentData): number {
  const ts = data.startedAt as { toMillis?: () => number } | null | undefined;
  return ts?.toMillis?.() ?? Number.POSITIVE_INFINITY;
}
